import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * The two things a `coloca-nest` error reliably carries, read once and correctly.
 *
 * Extracted from `users/utils/apiError.ts`, which had them first. They are not users-specific: every endpoint
 * in the backend answers through the same exception filter, so every consumer needs the same two reads.
 */

/** HTTP status, or `null` when the request never reached a response (network failure, abort). */
export function readApiStatus(error: unknown): number | null {
  if (HttpApiService.isAxiosError(error)) return error.response?.status ?? null;
  return null;
}

/**
 * The machine-readable `code` a handful of backend errors carry next to the sentence (a network rejection
 * detail, `amount_above_maximum`, …). Null for the vast majority that send only prose.
 */
export function readApiCode(error: unknown): string | null {
  if (!HttpApiService.isAxiosError(error)) return null;
  const payload = error.response?.data as { code?: unknown } | undefined;
  return typeof payload?.code === "string" && payload.code.length > 0 ? payload.code : null;
}

/**
 * Always an array, because the wire is not consistent: the global `ValidationPipe` rejects a body with
 * `message: string[]` (one sentence per broken rule) while every hand-thrown exception sends a plain string.
 *
 * `ApiError.message` is typed `string`, so anything reading `data.message` directly hands the ARRAY to React,
 * which renders it concatenated with no separator. Reachable while simply typing an amount: the quote is
 * debounced per keystroke and `send_amount` is pattern-validated, so a trailing dot is enough.
 */
export function readApiMessages(error: unknown): string[] {
  if (!HttpApiService.isAxiosError(error)) return [];
  const payload = error.response?.data as { message?: unknown } | undefined;
  const raw = payload?.message;
  if (Array.isArray(raw)) return raw.filter((item): item is string => typeof item === "string");
  if (typeof raw === "string") return [raw];
  return [];
}

/**
 * The sentence to SHOW A PERSON for a failure, with a guarantee `HttpApiService.getErrorMessage` does not
 * make: **it can never return `Error.message` of a request failure.**
 *
 * That distinction is the whole point. An `AxiosError` IS an `Error`, so `error instanceof Error` passes and
 * its `.message` is the literal `"Request failed with status code 400"` — which is exactly what reached a
 * user on `/app/cards`, rendered under the 2FA code boxes as if their authenticator code had been wrong.
 * `getErrorMessage` falls back to that same string and stays correct for logs and toasts built from a
 * known-good body; this is for the places where showing axios prose is a defect.
 *
 * - A request error → the backend's own sentence (a `ValidationPipe` array is joined), else `fallback`.
 * - Anything else → `Error.message`, which is how a caller deliberately hands over a translated sentence.
 * - No message anywhere (offline, abort, a thrown non-Error) → `fallback`.
 */
export function readHumanErrorMessage(error: unknown, fallback: string): string {
  if (HttpApiService.isAxiosError(error)) return readApiMessages(error).join(" ") || fallback;
  return error instanceof Error && error.message ? error.message : fallback;
}
